import { useCallback, useEffect, useState } from "react";
import type { YouTubePlayer } from "youtube-player/dist/types";

type Parameters = {
  adsManager?: google.ima.AdsManager;
  player?: YouTubePlayer;
};

export const useAdVolumeControl = (parameters: Parameters) => {
  const { adsManager, player } = parameters;
  const [isMute, setIsMute] = useState(true);

  const handleOnVolumeChange = useCallback(async () => {
    try {
      await player?.unMute();
      setIsMute(false);
    } catch (error) {
      console.error("Error in handling volume change event", error);
    }
  }, [player]);

  const handleOnMute = useCallback(async () => {
    try {
      await player?.mute();
      setIsMute(true);
    } catch (error) {
      console.error("Error in handling mute event", error);
    }
  }, [player]);

  const handleChangeVolume = useCallback(
    (mute: boolean) => {
      try {
        adsManager?.setVolume(mute ? 0 : 1);
        setIsMute(mute);
      } catch (error) {
        console.error("Error in changing ad volume", error);
      }
    },
    [adsManager],
  );

  const handleOnAdStarted = useCallback(async () => {
    try {
      const isMuted = await player?.isMuted();
      handleChangeVolume(isMuted ?? false);
    } catch (error) {
      console.error("Error in handling ad started event", error);
    }
  }, [handleChangeVolume, player]);

  useEffect(() => {
    if (!adsManager) return;
    adsManager.addEventListener(google.ima.AdEvent.Type.STARTED, handleOnAdStarted);
    return () => {
      adsManager.removeEventListener(google.ima.AdEvent.Type.STARTED, handleOnAdStarted);
    };
  }, [adsManager, handleOnAdStarted]);

  useEffect(() => {
    if (!adsManager) return;
    adsManager.addEventListener(google.ima.AdEvent.Type.VOLUME_CHANGED, handleOnVolumeChange);
    adsManager.addEventListener(google.ima.AdEvent.Type.VOLUME_MUTED, handleOnMute);

    return () => {
      adsManager.removeEventListener(google.ima.AdEvent.Type.VOLUME_CHANGED, handleOnVolumeChange);
      adsManager.removeEventListener(google.ima.AdEvent.Type.VOLUME_MUTED, handleOnMute);
    };
  }, [adsManager, handleOnMute, handleOnVolumeChange]);

  return {
    handleChangeVolume,
    isMute,
  };
};
